import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import banner from "../images/img3.jpg";
import "./usgs-style1.css";

const steps = ["Info", "Location", "Datasets", "Review"];

const purposeQuestions = [
  {
    question: "What is your primary reason for requesting this data?",
    options: ["Academic research", "Policy development", "Commercial project", "General interest"]
  },
  {
    question: "How will you use the data?",
    options: ["To develop tools", "For visualization", "To write papers or reports", "To support decision-making"]
  },
  {
    question: "What type of organization do you represent?",
    options: ["University/Research", "Government", "NGO", "Private sector"]
  },
  {
    question: "Do you plan to share the data further?",
    options: ["Yes, publicly", "Yes, with partners", "No", "Not sure"]
  },
  {
    question: "How often will you access the data?",
    options: ["One-time", "Occasionally", "Frequently", "Daily"]
  },
  {
    question: "What is your level of expertise in using geospatial datasets?",
    options: ["Beginner", "Intermediate", "Advanced", "Expert"]
  }
];

// 🔴 Required label helper component
const RequiredLabel = ({ children }) => (
  <label style={{ fontWeight: "bold", display: "block", marginBottom: "5px" }}>
    {children} <span style={{ color: "red" }}>*</span>
  </label>
);

const DataRequestForm = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ riverBasins: {}, countries: {} });
  const [datasetOptions, setDatasetOptions] = useState({ primary: [], water: [] });
  const [status, setStatus] = useState("");

  const [form, setForm] = useState({
    name: "", email: "", organization: "", jobTitle: "",
    locationType: "river", riverBasin: "", country: "",
    yearStart: "", yearEnd: "", temporal: "",
    datasets: [],
    geoArea: "",
    q1: "", q2: "", q3: "", q4: "", q5: "", q6: ""
  });

  useEffect(() => {
    const loadExcel = async () => {
      const res = await fetch("/assets/WA_Dashboard1.xlsx");
      const buf = await res.arrayBuffer();
      const workbook = XLSX.read(buf, { type: "buffer" });

      const toMap = (sheet, key) => {
        const json = XLSX.utils.sheet_to_json(sheet);
        const map = {};
        json.forEach(row => {
          const keyVal = row[key];
          if (keyVal) {
            map[keyVal.trim()] = {
              primary: row["Primary Data Sets"]?.split(",").map(s => s.trim()) || [],
              water: row["Water Data Products"]?.split(",").map(s => s.trim()) || [],
              temporal: row["Temporal Resolution"] || ""
            };
          }
        });
        return map;
      };

      setData({
        riverBasins: toMap(workbook.Sheets["River_basins"], "River basin"),
        countries: toMap(workbook.Sheets["Countries"], "Country")
      });
    };

    loadExcel();
  }, []);

  useEffect(() => {
    const { riverBasin, country, locationType } = form;
    const source = locationType === "river" ? data.riverBasins[riverBasin] : data.countries[country];
    if (!source) return;
    setDatasetOptions({ primary: source.primary, water: source.water });
    setForm(prev => ({ ...prev, temporal: source.temporal }));
  }, [form.riverBasin, form.country, form.locationType, data]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleDataset = ds => {
    setForm(prev => ({
      ...prev,
      datasets: prev.datasets.includes(ds)
        ? prev.datasets.filter(d => d !== ds)
        : [...prev.datasets, ds]
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 0:
        return (
          form.name.trim() &&
          form.email.trim() &&
          form.organization.trim() &&
          form.jobTitle.trim() &&
          form.q1 && form.q2 && form.q3 && form.q4 && form.q5 && form.q6 &&
          form.geoArea.trim()
        );
      case 1:
        return (
          (form.locationType === "river" ? form.riverBasin : form.country) &&
          form.yearStart.trim() &&
          form.yearEnd.trim()
        );
      case 2:
        return form.datasets.length > 0 && form.temporal.trim();
      default:
        return true;
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus("Sending...");
    try {
      const res = await fetch("/api/request-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const result = await res.json();
      setStatus(result.message || "✅ Request submitted successfully.");
    } catch {
      setStatus("❌ Submission failed.");
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            <RequiredLabel>Full Name</RequiredLabel>
            <input name="name" placeholder="Full Name" required onChange={handleChange} />

            <RequiredLabel>Email Address</RequiredLabel>
            <input name="email" type="email" placeholder="Email Address" required onChange={handleChange} />

            <RequiredLabel>Organization</RequiredLabel>
            <input name="organization" placeholder="Organization" required onChange={handleChange} />

            <RequiredLabel>Job Title</RequiredLabel>
            <input name="jobTitle" placeholder="Job Title" required onChange={handleChange} />

            <div className="purpose-section">
              {purposeQuestions.map((q, qi) => (
                <div key={qi} className="question-block" style={{ marginBottom: '28px' }}>
                  <label style={{ fontWeight: 'bold', fontSize: '20px', display: 'block', marginBottom: '12px' }}>
                    {`Q${qi + 1}. ${q.question}`} <span style={{ color: "red" }}>*</span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: '14px', columnGap: '30px' }}>
                    {q.options.map((opt, oi) => (
                      <label
                        key={oi}
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          fontSize: '18px',
                          gap: '10px',
                          cursor: 'pointer',
                          minWidth: '220px',
                          lineHeight: '1.4',
                        }}
                      >
                        <input
                          type="radio"
                          name={`q${qi + 1}`}
                          value={opt}
                          checked={form[`q${qi + 1}`] === opt}
                          onChange={handleChange}
                          required
                          style={{
                            width: '18px',
                            height: '18px',
                            accentColor: '#003366',
                            marginTop: '2px',
                          }}
                        />
                        <span><strong>{String.fromCharCode(97 + oi)})</strong> {opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div style={{ marginTop: '30px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '20px', display: 'block', marginBottom: '10px' }}>
                  Q7. What is the geographical area of interest? <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  name="geoArea"
                  rows="4"
                  placeholder="Please describe your area of interest (e.g., Nile Basin, Lake Tana region)"
                  onChange={handleChange}
                  value={form.geoArea}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '17px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                  }}
                  required
                />
              </div>
            </div>
          </>
        );

      case 1:
        return (
          <>
            <div className="location-select">
              <RequiredLabel>Select Location Type</RequiredLabel>
              <label>
                <input type="radio" name="locationType" value="river" checked={form.locationType === "river"} onChange={handleChange} />
                River Basin
              </label>
              <label>
                <input type="radio" name="locationType" value="country" checked={form.locationType === "country"} onChange={handleChange} />
                Country
              </label>
            </div>

            {form.locationType === "river" ? (
              <>
                <RequiredLabel>River Basin</RequiredLabel>
                <select name="riverBasin" onChange={handleChange} required>
                  <option value="">-- Select River Basin --</option>
                  {Object.keys(data.riverBasins).map((b, i) => <option key={i} value={b}>{b}</option>)}
                </select>
              </>
            ) : (
              <>
                <RequiredLabel>Country</RequiredLabel>
                <select name="country" onChange={handleChange} required>
                  <option value="">-- Select Country --</option>
                  {Object.keys(data.countries).map((c, i) => <option key={i} value={c}>{c}</option>)}
                </select>
              </>
            )}

            <div className="year-range">
              <RequiredLabel>Start Year</RequiredLabel>
              <input name="yearStart" placeholder="Start Year" onChange={handleChange} required />
              <RequiredLabel>End Year</RequiredLabel>
              <input name="yearEnd" placeholder="End Year" onChange={handleChange} required />
            </div>
          </>
        );

      case 2:
        return (
          <>
            <div className="dataset-section">
              <h4><RequiredLabel>📦 Primary Data Sets</RequiredLabel></h4>
              {datasetOptions.primary.map((ds, i) => (
                <label key={i}>
                  <input type="checkbox" checked={form.datasets.includes(ds)} onChange={() => toggleDataset(ds)} />
                  {ds}
                </label>
              ))}
            </div>
            <div className="dataset-section">
              <h4><RequiredLabel>💧 Water Data Products</RequiredLabel></h4>
              {datasetOptions.water.map((ds, i) => (
                <label key={i}>
                  <input type="checkbox" checked={form.datasets.includes(ds)} onChange={() => toggleDataset(ds)} />
                  {ds}
                </label>
              ))}
            </div>
            <div>
              <RequiredLabel>🕓 Temporal Range</RequiredLabel>
              <input
                name="temporal"
                placeholder="e.g. 2003 to 2017"
                value={form.temporal}
                onChange={handleChange}
              />
            </div>
          </>
        );

      case 3:
        return (
          <div className="review-block">
            <p><strong>Name:</strong> {form.name}</p>
            <p><strong>Email:</strong> {form.email}</p>
            <p><strong>Organization:</strong> {form.organization}</p>
            <p><strong>Job Title:</strong> {form.jobTitle}</p>
            <p><strong>Location:</strong> {form.locationType === "river" ? form.riverBasin : form.country}</p>
            <p><strong>Years:</strong> {form.yearStart} - {form.yearEnd}</p>
            <p><strong>Datasets:</strong> {form.datasets.join(", ")}</p>
            <p><strong>Temporal:</strong> {form.temporal}</p>
            <p><strong>Geo Area:</strong> {form.geoArea}</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="explorer-container">
      <div className="explorer-banner">
        <img src={banner} alt="Banner" className="banner-img" />
        <div className="explorer-title-overlay">
          <h1>🌍 Data Access Explorer</h1>
          <p>Please complete the form to request access to datasets.</p>
        </div>
      </div>

      <form className="explorer-form" onSubmit={handleSubmit}>
        <div className="tab-header">
          {steps.map((s, i) => (
            <div key={i} className={`tab-item ${step === i ? "active" : ""}`}>{s}</div>
          ))}
        </div>

        {renderStep()}

        <div className="form-actions">
          {step > 0 && <button type="button" onClick={() => setStep(step - 1)}>⬅ Back</button>}
          {step < steps.length - 1 ? (
            <button
              type="button"
              onClick={() => {
                if (validateStep()) {
                  setStep(step + 1);
                } else {
                  alert("Please complete all required fields before proceeding.");
                }
              }}
            >
              Next ➡
            </button>
          ) : (
            <button type="submit">📩 Submit</button>
          )}
        </div>
        <p className="form-status">{status}</p>
      </form>
    </div>
  );
};

export default DataRequestForm;
